import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Plus, Loader2, Edit, Trash2, Calendar, Clock, Users, X, PlusCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { ImageUpload } from "../../components/ImageUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS_OF_WEEK = [
  { value: 0, label: "Monday" },
  { value: 1, label: "Tuesday" },
  { value: 2, label: "Wednesday" },
  { value: 3, label: "Thursday" },
  { value: 4, label: "Friday" },
  { value: 5, label: "Saturday" },
  { value: 6, label: "Sunday" },
];

export const AdminClasses = () => {
  const [classes, setClasses] = useState([]);
  const [styles, setStyles] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [classToDelete, setClassToDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [activeTab, setActiveTab] = useState("active");
  
  // New style dialog state
  const [styleDialogOpen, setStyleDialogOpen] = useState(false);
  const [newStyleData, setNewStyleData] = useState({ name: "", description: "" });
  const [creatingStyle, setCreatingStyle] = useState(false);
  
  const emptyForm = {
    name: "",
    slug: "",
    description: "",
    dance_style_id: "",
    instructor_id: "",
    difficulty_level: "all_levels",
    duration_minutes: 60,
    max_capacity: 20,
    price: 1500,
    discount_percentage: "",
    image: "",
    studio_name: "Empire Dance Studios",
    occurrences: [],
    is_active: true,
    is_redeemable_with_package: true,
  };
  
  const [formData, setFormData] = useState(emptyForm);
  const [newOccurrence, setNewOccurrence] = useState({ day_of_week: 0, start_time: "10:00", end_time: "11:00" });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [classesRes, stylesRes, instructorsRes] = await Promise.all([
        // Load the complete class set so client-side active/inactive tabs and
        // summary counts include classes beyond the API's default page size.
        axios.get(`${API}/classes?limit=100`),
        axios.get(`${API}/styles`),
        axios.get(`${API}/instructors`),
      ]);
      
      setClasses(classesRes.data.classes || classesRes.data || []);
      setStyles(stylesRes.data || []);
      setInstructors(instructorsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load classes");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingClass(null);
    setNewOccurrence({ day_of_week: 0, start_time: "10:00", end_time: "11:00" });
  };

  const handleOpenDialog = (cls = null) => {
    if (cls) {
      setEditingClass(cls);
      setFormData({
        name: cls.name || "",
        slug: cls.slug || "",
        description: cls.description || "",
        dance_style_id: cls.dance_style_id || "",
        instructor_id: cls.instructor_id || "",
        difficulty_level: cls.difficulty_level || "all_levels",
        duration_minutes: cls.duration_minutes || 60,
        max_capacity: cls.max_capacity || 20,
        price: cls.price || 1500,
        discount_percentage: cls.discount_percentage ?? "",
        image: cls.image || "",
        studio_name: cls.studio_name || "Empire Dance Studios",
        occurrences: cls.occurrences || [],
        is_active: cls.is_active !== false,
        is_redeemable_with_package: cls.is_redeemable_with_package !== false,
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const addOccurrence = () => {
    const endParts = newOccurrence.start_time.split(":");
    const endHour = parseInt(endParts[0]) + Math.floor(formData.duration_minutes / 60);
    const endMin = parseInt(endParts[1]) + (formData.duration_minutes % 60);
    const calculatedEnd = `${String(endHour).padStart(2, "0")}:${String(endMin % 60).padStart(2, "0")}`;
    
    setFormData({
      ...formData,
      occurrences: [
        ...formData.occurrences,
        { ...newOccurrence, end_time: newOccurrence.end_time || calculatedEnd }
      ]
    });
    setNewOccurrence({ day_of_week: 0, start_time: "10:00", end_time: "11:00" });
  };

  const removeOccurrence = (index) => {
    setFormData({
      ...formData,
      occurrences: formData.occurrences.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const slug = formData.slug || formData.name.toLowerCase().replace(/\s+/g, "-");
      const payload = {
        ...formData,
        slug,
        price: Number(formData.price),
        discount_percentage:
          formData.discount_percentage === "" ? null : Number(formData.discount_percentage),
        duration_minutes: Number(formData.duration_minutes),
        max_capacity: Number(formData.max_capacity),
        is_redeemable_with_package: formData.is_redeemable_with_package,
      };

      if (editingClass) {
        await axios.put(`${API}/admin/classes/${editingClass.id}`, payload);
        toast.success("Class updated successfully!");
      } else {
        await axios.post(`${API}/admin/classes`, payload);
        toast.success("Class created successfully!");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save class");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = (cls) => {
    setClassToDelete(cls);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!classToDelete) return;
    
    try {
      await axios.delete(`${API}/admin/classes/${classToDelete.id}`);
      toast.success(`"${classToDelete.name}" has been deleted`);
      setDeleteDialogOpen(false);
      setClassToDelete(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete class");
    }
  };

  const handleToggleActive = (cls) => {
    if (cls.is_active !== false) {
      toast.warning(`Deactivate "${cls.name}"?`, {
        description: "This class will no longer appear on the public schedule.",
        action: {
          label: "Deactivate",
          onClick: async () => {
            try {
              await axios.put(`${API}/admin/classes/${cls.id}`, { is_active: false });
              toast.success("Class deactivated");
              fetchData();
            } catch {
              toast.error("Failed to update class");
            }
          },
        },
        cancel: { label: "Cancel", onClick: () => {} },
      });
      return;
    }
    axios.put(`${API}/admin/classes/${cls.id}`, { is_active: true })
      .then(() => { toast.success("Class activated"); fetchData(); })
      .catch(() => toast.error("Failed to update class"));
  };

  // Handle style selection - open dialog if "new" is selected
  const handleStyleChange = (value) => {
    if (value === "__new__") {
      setStyleDialogOpen(true);
    } else {
      setFormData({ ...formData, dance_style_id: value });
    }
  };

  // Create new dance style
  const handleCreateStyle = async (e) => {
    e.preventDefault();
    if (!newStyleData.name.trim()) {
      toast.error("Style name is required");
      return;
    }
    
    setCreatingStyle(true);
    try {
      const slug = newStyleData.name.toLowerCase().replace(/\s+/g, "-");
      const res = await axios.post(`${API}/admin/styles`, {
        name: newStyleData.name,
        slug,
        description: newStyleData.description,
        is_active: true
      });
      
      // Add to styles list and select it
      const newStyle = res.data;
      setStyles([...styles, newStyle]);
      setFormData({ ...formData, dance_style_id: newStyle.id });
      
      toast.success(`"${newStyleData.name}" style created!`);
      setStyleDialogOpen(false);
      setNewStyleData({ name: "", description: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create style");
    } finally {
      setCreatingStyle(false);
    }
  };

  const levelColors = {
    beginner: "bg-green-500/20 text-green-400",
    intermediate: "bg-yellow-500/20 text-yellow-400",
    advanced: "bg-red-500/20 text-red-400",
    all_levels: "bg-empire-gold/20 text-empire-gold",
  };

  const getInstructorName = (instructorId) => {
    const instructor = instructors.find(i => i.id === instructorId);
    return instructor?.name || "Not assigned";
  };

  const getStyleName = (styleId) => {
    const style = styles.find(s => s.id === styleId);
    return style?.name || "";
  };

  const handleInstructorChange = (instructorId) => {
    const instructor = instructors.find(i => i.id === instructorId);
    const isDefault = instructor?.is_default === true;
    setFormData({ 
      ...formData, 
      instructor_id: instructorId,
      is_redeemable_with_package: isDefault
    });
  };

  return (
    <div data-testid="admin-classes">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 md:mb-8 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">Classes</h1>
          <p className="text-neutral-400">Manage dance classes and schedules</p>
        </div>
        
        <Button className="btn-empire" onClick={() => handleOpenDialog()}>
          <Plus className="w-4 h-4 mr-2" />
          Add Class
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-white">{classes.length}</div>
            <p className="text-sm text-neutral-400">Total Classes</p>
          </CardContent>
        </Card>
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-400">
              {classes.filter(c => c.is_active !== false).length}
            </div>
            <p className="text-sm text-neutral-400">Active</p>
          </CardContent>
        </Card>
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-empire-gold">
              {classes.reduce((sum, c) => sum + (c.occurrences?.length || 0), 0)}
            </div>
            <p className="text-sm text-neutral-400">Weekly Sessions</p>
          </CardContent>
        </Card>
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-400">
              {styles.length}
            </div>
            <p className="text-sm text-neutral-400">Dance Styles</p>
          </CardContent>
        </Card>
      </div>

      {/* Active/Inactive Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="bg-neutral-900 border border-neutral-800">
          <TabsTrigger value="active" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
            Active ({classes.filter(c => c.is_active !== false).length})
          </TabsTrigger>
          <TabsTrigger value="inactive" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
            Inactive ({classes.filter(c => c.is_active === false).length})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Classes Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
        </div>
      ) : (() => {
        const filteredClasses = activeTab === "active" 
          ? classes.filter(c => c.is_active !== false)
          : classes.filter(c => c.is_active === false);
        
        return filteredClasses.length === 0 ? (
          <Card className="card-empire">
            <CardContent className="py-12 text-center">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-neutral-600" />
              <p className="text-neutral-500">
                {activeTab === "active" ? "No active classes. Create your first class!" : "No inactive classes."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClasses.map((cls, index) => (
            <motion.div
              key={cls.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className={`card-empire overflow-hidden ${cls.is_active === false ? "opacity-60" : ""}`}>
                <div className="relative h-40">
                  <img
                    src={cls.image || "https://images.unsplash.com/photo-1508700929628-666bc8bd84ea?w=400"}
                    alt={cls.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <Badge className={`absolute top-3 left-3 ${levelColors[cls.difficulty_level]}`}>
                    {cls.difficulty_level?.replace("_", " ")}
                  </Badge>
                  {cls.is_active === false && (
                    <Badge className="absolute top-3 right-3 bg-red-500/20 text-red-400">
                      Inactive
                    </Badge>
                  )}
                </div>
                <CardContent className="p-4">
                  <h3 className="text-lg font-bold text-white mb-1">{cls.name}</h3>
                  <p className="text-sm text-neutral-400 mb-2">
                    {getInstructorName(cls.instructor_id)}
                  </p>
                  
                  {/* Occurrences */}
                  {cls.occurrences && cls.occurrences.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {cls.occurrences.map((occ, i) => (
                        <Badge key={i} variant="outline" className="text-xs border-neutral-700">
                          {DAYS_OF_WEEK.find(d => d.value === occ.day_of_week)?.label?.slice(0, 3)} {occ.start_time}
                        </Badge>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3 text-xs text-neutral-500 mb-3">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {cls.duration_minutes}min
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {cls.max_capacity}
                    </span>
                    {getStyleName(cls.dance_style_id) && (
                      <Badge variant="outline" className="text-xs border-neutral-700">
                        {getStyleName(cls.dance_style_id)}
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-neutral-800">
                    <span className="text-empire-gold font-bold">
                      KES {cls.price?.toLocaleString()}
                    </span>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={cls.is_active !== false}
                        onCheckedChange={() => handleToggleActive(cls)}
                        className="scale-75"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenDialog(cls)}
                        className="text-neutral-400 hover:text-white h-8 w-8"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => confirmDelete(cls)}
                        className="text-neutral-400 hover:text-red-400 h-8 w-8"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
        );
      })()}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
        <DialogContent className="bg-neutral-900 border-neutral-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingClass ? "Edit Class" : "Create New Class"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Class Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-empire"
                  placeholder="e.g., Hip Hop Fundamentals"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-white">Choreographer/Instructor *</Label>
                <Select
                  value={formData.instructor_id}
                  onValueChange={handleInstructorChange}
                >
                  <SelectTrigger className="bg-neutral-900 border-neutral-800">
                    <SelectValue placeholder="Select choreographer" />
                  </SelectTrigger>
                  <SelectContent>
                    {instructors.map((instructor) => (
                      <SelectItem key={instructor.id} value={instructor.id}>
                        {instructor.name} {instructor.is_default && "(Default)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Package Redeemable Toggle */}
            <div className="flex items-center justify-between p-3 bg-neutral-800/50 rounded-lg">
              <div>
                <Label className="text-white">Redeemable with Package</Label>
                <p className="text-xs text-neutral-400 mt-0.5">
                  Allow package holders to use credits for this class
                </p>
              </div>
              <Switch
                checked={formData.is_redeemable_with_package}
                onCheckedChange={(checked) => setFormData({ ...formData, is_redeemable_with_package: checked })}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-empire min-h-[60px]"
                placeholder="Class description..."
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Dance Style</Label>
                <Select
                  value={formData.dance_style_id}
                  onValueChange={handleStyleChange}
                >
                  <SelectTrigger className="bg-neutral-900 border-neutral-800">
                    <SelectValue placeholder="Select style" />
                  </SelectTrigger>
                  <SelectContent>
                    {styles.map((style) => (
                      <SelectItem key={style.id} value={style.id}>
                        {style.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="__new__" className="text-empire-gold border-t border-neutral-800 mt-1 pt-1">
                      <span className="flex items-center gap-2">
                        <PlusCircle className="w-4 h-4" />
                        Add New Style
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Level</Label>
                <Select
                  value={formData.difficulty_level}
                  onValueChange={(value) => setFormData({ ...formData, difficulty_level: value })}
                >
                  <SelectTrigger className="bg-neutral-900 border-neutral-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                    <SelectItem value="all_levels">All Levels</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Duration (min)</Label>
                <Input
                  type="number"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                  className="input-empire"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">Price (KES)</Label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="input-empire"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Discount (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.discount_percentage}
                  onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value })}
                  className="input-empire"
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Discounted Price</Label>
                <div className="flex items-center gap-2 px-3 h-10 rounded-lg border border-neutral-800 bg-neutral-800/60">
                  {Number(formData.discount_percentage) > 0 ? (
                    <>
                      <span className="text-empire-gold font-semibold">
                        KES {Math.round(Number(formData.price || 0) * (1 - Number(formData.discount_percentage) / 100)).toLocaleString()}
                      </span>
                      <span className="text-neutral-500 line-through text-sm">
                        KES {Number(formData.price || 0).toLocaleString()}
                      </span>
                    </>
                  ) : (
                    <span className="text-neutral-500 text-sm">No discount applied</span>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Max Capacity</Label>
                <Input
                  type="number"
                  value={formData.max_capacity}
                  onChange={(e) => setFormData({ ...formData, max_capacity: e.target.value })}
                  className="input-empire"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-white">Studio</Label>
                <Input
                  value={formData.studio_name}
                  className="input-empire bg-neutral-800"
                  disabled
                />
              </div>
            </div>

            {/* Occurrences Section */}
            <div className="space-y-3 p-4 bg-neutral-800/50 rounded-lg">
              <Label className="text-white text-base font-semibold">Weekly Schedule (Occurrences)</Label>
              <p className="text-xs text-neutral-400 mb-2">Add the days and times when this class occurs each week</p>
              
              {/* Existing occurrences */}
              {formData.occurrences.length > 0 && (
                <div className="space-y-2 mb-4">
                  {formData.occurrences.map((occ, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-neutral-900 rounded">
                      <Badge className="bg-empire-gold/20 text-empire-gold">
                        {DAYS_OF_WEEK.find(d => d.value === occ.day_of_week)?.label}
                      </Badge>
                      <span className="text-white text-sm">{occ.start_time} - {occ.end_time}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeOccurrence(index)}
                        className="ml-auto h-6 w-6 text-neutral-400 hover:text-red-400"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add new occurrence */}
              <div className="flex flex-wrap items-end gap-2">
                <div className="space-y-1">
                  <Label className="text-xs text-neutral-400">Day</Label>
                  <Select
                    value={String(newOccurrence.day_of_week)}
                    onValueChange={(value) => setNewOccurrence({ ...newOccurrence, day_of_week: parseInt(value) })}
                  >
                    <SelectTrigger className="w-32 bg-neutral-900 border-neutral-700 h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS_OF_WEEK.map((day) => (
                        <SelectItem key={day.value} value={String(day.value)}>
                          {day.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-neutral-400">Start</Label>
                  <Input
                    type="time"
                    value={newOccurrence.start_time}
                    onChange={(e) => setNewOccurrence({ ...newOccurrence, start_time: e.target.value })}
                    className="w-28 bg-neutral-900 border-neutral-700 h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-neutral-400">End</Label>
                  <Input
                    type="time"
                    value={newOccurrence.end_time}
                    onChange={(e) => setNewOccurrence({ ...newOccurrence, end_time: e.target.value })}
                    className="w-28 bg-neutral-900 border-neutral-700 h-9"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addOccurrence}
                  className="border-empire-gold text-empire-gold hover:bg-empire-gold/10"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>

            <ImageUpload
              label="Class Image"
              value={formData.image}
              onChange={(url) => setFormData({ ...formData, image: url })}
              folder="classes"
              placeholder="Upload or paste image URL"
            />

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label className="text-white">Active (visible to guests)</Label>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => { resetForm(); setDialogOpen(false); }}
                className="flex-1 border-neutral-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 btn-empire"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : editingClass ? "Update Class" : "Create Class"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-neutral-900 border-neutral-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Class</AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-400">
              Are you sure you want to delete "{classToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-neutral-700">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New Dance Style Dialog */}
      <Dialog open={styleDialogOpen} onOpenChange={setStyleDialogOpen}>
        <DialogContent className="bg-neutral-900 border-neutral-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Create New Dance Style</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateStyle} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">Style Name *</Label>
              <Input
                value={newStyleData.name}
                onChange={(e) => setNewStyleData({ ...newStyleData, name: e.target.value })}
                className="input-empire"
                placeholder="e.g., Bachata, Kizomba, Dancehall"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-white">Description</Label>
              <Textarea
                value={newStyleData.description}
                onChange={(e) => setNewStyleData({ ...newStyleData, description: e.target.value })}
                className="input-empire min-h-[80px]"
                placeholder="Brief description of this dance style..."
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setStyleDialogOpen(false);
                  setNewStyleData({ name: "", description: "" });
                }}
                className="flex-1 border-neutral-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={creatingStyle}
                className="flex-1 btn-empire"
              >
                {creatingStyle ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Style"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
